import React, { useEffect, useState } from "react";
import { FieldArray, useFormikContext, Field } from "formik";
import Select from "./FormikSelectField";
import Input from "./InputField";
import FormFieldOptionArray from "./FormFieldOptionArray";
import { useGetFormFieldTypes } from "../hooks/forms/useGet";
import { IoCloseCircle } from "react-icons/io5";

const FormFieldArray = ({ id }) => {
  const { values, setFieldValue } = useFormikContext();
  const [optionValue, setOptionValue] = useState("");

  const {
    data: formFieldTypes,
    isSuccess: gotFieldTypes,
  } = useGetFormFieldTypes();

  const formFieldTypeOptions = formFieldTypes?.map((item) => ({
    value: item.formFieldTypeId,
    name: item.fieldTypeName,
  }));

  const handleAddOption = (index) => {
    if (!optionValue) return;
    const optionsPath = `formSections[${id}].formFields[${index}].formFieldOptions`;
    const currentOptions =
      values.formSections[id].formFields[index].formFieldOptions;
    const newOption = { fieldValue: optionValue };

    setFieldValue(optionsPath, [...currentOptions, newOption]);
    setOptionValue("");
  };

  const handleDeleteOption = (optionIndex, index) => {
    const optionsPath = `formSections[${id}].formFields[${index}].formFieldOptions`;
    const currentOptions =
      values.formSections[id].formFields[index].formFieldOptions;

    currentOptions.splice(optionIndex, 1);
    setFieldValue(optionsPath, [...currentOptions]);
  };

  // --- Ensure default isImage + imageSize for type 13 ---
  useEffect(() => {
    const section = values?.formSections?.[id];
    const fields = section?.formFields ?? [];
    fields.forEach((f, idx) => {
      const isType13 =
        f?.formFieldTypeId === 13 || f?.formFieldTypeId === "13";

      if (isType13) {
        // force isImage true
        setFieldValue(
          `formSections[${id}].formFields.${idx}.isImage`,
          true
        );
        // ensure imageSize default
        const hasImageSize =
          typeof f?.imageSize === "string" && f.imageSize.trim().length > 0;
     
      } else {
        // force isImage false for all others
        setFieldValue(
          `formSections[${id}].formFields.${idx}.isImage`,
          false
        );
      }
    });
  }, [id, values?.formSections, setFieldValue]);
  // ------------------------------------------------------

  return (
    <FieldArray name={`formSections[${id}].formFields`}>
      {({ insert, remove, push }) => (
        <div
          className="w-full gap-4 my-5"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(400px,1fr))",
          }}
        >
          {values.formSections[id].formFields?.length > 0 &&
            values.formSections[id].formFields?.map((formfields, index) => (
              <div
                className="flex w-full flex-col space-y-3 border p-3 border-gray-400 rounded bg-zinc-50 "
                style={{ alignSelf: "flex-start" }}
                key={index}
              >
                <div className="w-full py-3 flex justify-between items-center relative">
                  <span>
                    {values.formSections[id].formFields[index].label.length ===
                    0
                      ? `Field ${index + 1}`
                      : values.formSections[id].formFields[index].label}
                  </span>

                  <IoCloseCircle
                    onClick={() => remove(index)}
                    className="absolute -right-1 -top-1 text-red-600 text-2xl self-top cursor-pointer transition-all ease-in-out hover:scale-110"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    name={`formSections[${id}].formFields.${index}.label`}
                    id={`formSections[${id}].formFields.${index}.label`}
                    placeholder="Label"
                    label="Label"
                  />

                  {gotFieldTypes && (
                    <Select
                      name={`formSections[${id}].formFields.${index}.formFieldTypeId`}
                      id={`formSections[${id}].formFields.${index}.formFieldTypeId`}
                      label="Type"
                      defaultValue={
                        values.formSections[id].formFields[index]
                          .formFieldTypeId
                      }
                      required
                      options={formFieldTypeOptions}
                    />
                  )}

                  {/* Image fields (type 13) */}
                  {(() => {
                    const typeId =
                      values.formSections[id].formFields[index]
                        .formFieldTypeId;
                    const isType13 = typeId === 13 || typeId === "13";
                    return isType13 ? (
                      <>
                        <Input
                          name={`formSections[${id}].formFields.${index}.imageSize`}
                          id={`formSections[${id}].formFields.${index}.imageSize`}
                          placeholder="100 x 100"
                          label="Image Size (e.g., 100 x 100)"
                        />
                      </>
                    ) : null;
                  })()}

                  {/* Options builder for types 5 or 1001 */}
                  {(values.formSections[id].formFields[index].formFieldTypeId ==
                    "5" ||
                    values.formSections[id].formFields[index]
                      .formFieldTypeId == "1001" ||
                    values.formSections[id].formFields[index]
                      .formFieldTypeId === 5 ||
                    values.formSections[id].formFields[index]
                      .formFieldTypeId === 1001) && (
                    <FieldArray
                      name={`formSections[${id}].formFields[${index}].formFieldOptions`}
                    >
                      {({ form, push }) => (
                        <div className="col-span-2">
                          <div className="flex justify-between items-center">
                            <div className="flex-1 mr-2">
                              <input
                                value={optionValue}
                                onChange={(e) =>
                                  setOptionValue(e.target.value)
                                }
                                placeholder="Option 1"
                                className="form-input"
                              />
                            </div>

                            <button
                              type="button"
                              className="bg-green-500 text-white min-h-[4vh] border-slate-600 text-sm px-2 py-1.5 mb-2 rounded flex-1 mb-[-2px]"
                              onClick={() => handleAddOption(index)}
                            >
                              Add
                            </button>
                          </div>

                          {values.formSections[id].formFields[
                            index
                          ].formFieldOptions.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className="flex justify-between items-center mt-2"
                            >
                              <span className="flex-1">
                                {option.fieldValue}
                              </span>
                              <button
                                type="button"
                                className="bg-red-500 text-white min-h-[4vh] border-slate-600 text-sm px-2 py-1.5 mb-2 rounded"
                                onClick={() =>
                                  handleDeleteOption(optionIndex, index)
                                }
                              >
                                Delete Option
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </FieldArray>
                  )}

                  <Input
                    type="checkbox"
                    name={`formSections[${id}].formFields.${index}.activeStatus`}
                    id={`formSections[${id}].formFields.${index}.activeStatus`}
                    label={"Active Status"}
                  />

                  <Input
                    type="checkbox"
                    name={`formSections[${id}].formFields.${index}.required`}
                    id={`formSections[${id}].formFields.${index}.required`}
                    label="Required"
                  />

                  <Input
                    type="checkbox"
                    name={`formSections[${id}].formFields.${index}.isCalculated`}
                    id={`formSections[${id}].formFields.${index}.isCalculated`}
                    label="Is Calculated"
                  />
                </div>

                {values.formSections[id].formFields[index].isCalculated && (
                  <Input
                    name={`formSections[${id}].formFields.${index}.calculationFormula`}
                    id={`formSections[${id}].formFields.${index}.calculationFormula`}
                    placeholder="[Label] = [field1_Label]*[field2_label]"
                    label="Calculation Formula"
                  />
                )}

                {values.formSections[id].formFields[index].formFieldTypeId ===
                  "10" && (
                  <FormFieldOptionArray sectionId={id} fieldId={index} />
                )}
              </div>
            ))}

          <button
            type="button"
            className="border-dashed border-2 min-h-[14vh] border-slate-600 text-sm px-2 py-1.5 mb-2 rounded"
            onClick={() =>
              push({
                label: "",
                activeStatus: true,
                formFieldTypeId: 0,
                required: false,
                formFieldOptions: [],
                imageSize: "",
                isImage: false, // default false; effect will flip to true if type==13
              })
            }
          >
            Add Field
          </button>
        </div>
      )}
    </FieldArray>
  );
};

export default FormFieldArray;
